import React from 'react';

/**
 * Tela de manutenção — resposta padrão da plataforma para contas inativas.
 *
 * Discreta de propósito: nada de erro em vermelho, nada de "conta bloqueada".
 * É a mesma cara de uma janela de manutenção programada, com a identidade da
 * casa (oliva/âmbar, Poppins) e uma única ação de saída no rodapé.
 */
interface MaintenanceScreenProps {
    /** Momento da última checagem de disponibilidade — a linha some se não vier. */
    ultimaVerificacao?: Date;
    /** Ação discreta do rodapé (ex.: "Sair" no app, "Voltar" no login). */
    acao?: { label: string; onClick: () => void };
    /** Marca da tela: VigorHub (padrão) ou Vigor Energy, no Portal do Consumidor. */
    marca?: 'vigorhub' | 'portal';
}

const MARCAS = {
    vigorhub: {
        logo: '/vigorhub_logo_transparent.png',
        alt: 'VigorHub',
        produto: 'ao VigorHub',
        // logo escura: no tema dark vira branca
        classe: 'h-11 dark:brightness-0 dark:invert dark:opacity-80',
    },
    portal: {
        logo: '/LOGO-01.png',
        alt: 'Vigor Energy',
        produto: 'ao Portal do Consumidor',
        classe: 'h-14',
    },
} as const;

const horaCurta = (data: Date) =>
    data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ ultimaVerificacao, acao, marca = 'vigorhub' }) => (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-12 bg-[#F9FAFB] dark:bg-[#1d220f] relative overflow-hidden">
        <style>{`
            @keyframes vigor-maint-sweep {
                0%   { transform: translateX(-110%); }
                100% { transform: translateX(410%); }
            }
            @keyframes vigor-maint-breathe {
                0%, 100% { opacity: 0.35; transform: scale(0.85); }
                50%      { opacity: 1;    transform: scale(1); }
            }
            .vigor-maint-sweep   { animation: vigor-maint-sweep 3.2s cubic-bezier(0.45, 0, 0.35, 1) infinite; }
            .vigor-maint-breathe { animation: vigor-maint-breathe 2.4s ease-in-out infinite; }
            @media (prefers-reduced-motion: reduce) {
                .vigor-maint-sweep, .vigor-maint-breathe { animation: none; }
            }
        `}</style>

        {/* Halo suave atrás do cartão — atmosfera, sem chamar atenção */}
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(55% 45% at 50% 8%, rgba(95,108,55,0.10), transparent 70%)' }}
        />

        <div
            role="status"
            aria-live="polite"
            className="relative w-full max-w-md rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-white/[0.03] px-8 py-10 sm:px-10 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_16px_48px_-24px_rgba(16,24,40,0.25)] animate-fade-in"
        >
            <img
                src={MARCAS[marca].logo}
                alt={MARCAS[marca].alt}
                className={`mx-auto object-contain select-none opacity-90 ${MARCAS[marca].classe}`}
                draggable={false}
            />

            <div className="mt-8 flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                    <span className="vigor-maint-breathe w-1.5 h-1.5 rounded-full bg-secondary" />
                    Manutenção
                </span>
            </div>

            <h1 className="mt-5 text-center font-poppins text-xl font-semibold text-gray-900 dark:text-gray-100">
                Estamos em manutenção
            </h1>
            <p className="mt-3 text-center text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                O acesso {MARCAS[marca].produto} está temporariamente indisponível enquanto
                concluímos uma atualização. Nada do seu trabalho foi perdido — tente novamente mais tarde.
            </p>

            {/* Faixa de progresso indeterminada, fina o bastante pra não gritar */}
            <div className="mt-8 h-[2px] w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                <div className="vigor-maint-sweep h-full w-1/4 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent dark:via-secondary" />
            </div>

            <div className="mt-6 flex items-center justify-between gap-4 text-[11px] text-gray-400 dark:text-gray-500">
                <span>{ultimaVerificacao ? `Última verificação às ${horaCurta(ultimaVerificacao)}` : ' '}</span>
                {acao && (
                    <button
                        type="button"
                        onClick={acao.onClick}
                        className="font-medium text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-secondary underline underline-offset-4 decoration-gray-200 dark:decoration-white/20 hover:decoration-current transition-colors"
                    >
                        {acao.label}
                    </button>
                )}
            </div>
        </div>
    </div>
);

export default MaintenanceScreen;
